import type { DeviceSettings } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  InfoButton,
  Label,
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import styles from "./Display.module.scss"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import { useSliderInputProps } from "src/hooks"

const polyglotPrefix = "pages.settings.display.graph_icon_scale."

const sharedProps = {
  min: 0.5,
  max: 2,
  step: 0.1,
}

interface Props {
  updatedAt?: string;
  updateDeviceSettings: (settings: Partial<DeviceSettings>) => void;
}

export function GraphIconScale(props: Props) {
  const {
    updatedAt,
    updateDeviceSettings,
  } = props

  const polyglot = usePolyglot()
  const { currentDeviceSettings } = useFollowedUsers()
  const currentGraphIconScale = currentDeviceSettings.graph_icon_scale

  const onValueChange = useCallback(
    (graphIconScale: DeviceSettings["graph_icon_scale"]) => {
      updateDeviceSettings({
        graph_icon_scale: graphIconScale,
      })
    },
    [updateDeviceSettings],
  )

  const {
    inputValue,
    sliderValue,
    onSliderChange,
    onNumberChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    value: currentGraphIconScale,
    updatedAt,
    onChange: onValueChange,
    min: sharedProps.min,
    max: sharedProps.max,
    inputStep: sharedProps.step,
    sliderStep: sharedProps.step,
  })

  return (
    <div>
      <Label>
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}title`)}>
          {polyglot.t(`${polyglotPrefix}description`)}
        </InfoButton>
      </Label>
      <div className={styles.inputContainer}>
        <SliderValue
          sizePreset="large"
          alignmentPreset="start"
        >
          <SliderValueInput
            {...sharedProps}
            value={inputValue}
            onChange={onNumberChange}
            onBlur={onNumberBlur}
            onKeyDown={onNumberKeyDown}
            placeholder={sharedProps.min.toLocaleString()}
            id="graph-icon-scale-number"
          />
          <SliderValueLabelContainer unit="x" />
        </SliderValue>
        <Slider
          className={styles.rangeInputSlider}
          id="graph-icon-scale-slider"
        >
          <SliderInput
            {...sharedProps}
            value={sliderValue}
            onChange={onSliderChange}
          />
          <SliderTracks
            min={sharedProps.min}
            max={sharedProps.max}
          />
        </Slider>
      </div>
    </div>
  )
}
